import {
  Accordion,
  AccordionItem,
  Box,
  Button,
  Card,
  Display1,
  Flex,
  Grid,
  GridItem,
  Label1,
  Tag,
} from '@sainsburys-tech/fable'
import {
  Add,
  AlertCircleFilled,
  Clock,
  Edit,
  InfoCircle,
} from '@sainsburys-tech/icons'
import moment, { MomentInput } from 'moment'
import React from 'react'

import { getUserStore } from '../../../utils/localStore'
import { IBatchTemperature } from '../../types/IBatchTemperature'
import { IBatchTemperaturesResponse } from '../../types/IBatchTemperaturesResponse'
import { ICounterAvailability } from '../../types/ICounterAvailability'
import BatchItemTable from './BatchItemTable'
import { SummaryCard } from './SummaryCard'
import TemperatureCheck from './TemperatureCheck'

moment.locale('en-GB')

interface IProps {
  batchDate: MomentInput
  batchTemperatures: IBatchTemperaturesResponse
  counterAvailability: ICounterAvailability | undefined
  hasSingleCabinetType?: boolean | undefined
}

const TemperatureCheckView = (props: IProps) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600
  const getPrintFooter = () => {
    const store = getUserStore()
    const printDateTime = new Date()
    return `Report generated at ${printDateTime.getDate()}/${
      printDateTime.getMonth() + 1
    }/${printDateTime.getFullYear()} 
    ${printDateTime.getHours()}:${printDateTime.getMinutes()} for store ${store.storeId}-${store.storeName}.`
  }

  const renderAccordionItemCard = (batch: IBatchTemperature) => (
    <Card
      orientation='vertical'
      style={{
        background:
          'var(--fable-color-background-secondary-default) !important',
        padding: 12,
        boxSizing: 'border-box',
      }}
    >
      {batch.isActive !== false && renderTemperatureChecks(batch)}
      <BatchItemTable
        batchItems={batch.items}
        isPizza={batch.programId === null}
      />
    </Card>
  )

  const renderBatchTitle = (batch: IBatchTemperature, i: number) => {
    const isb = batch.isIsbProduct === true
    const pizza = batch.isPizza === true
    const batchText = `${pizza ? 'Pizza' : ''}${pizza && isb ? ' and ' : ''}${isb ? 'ISB' : ''}`

    return (
      <span data-testid='batch-title'>
        {batch.programId == null &&
          `${batchText} - ${moment.utc(batch.productionTime).local().format('H:mm')}`}
        {batch.programId !== null &&
          `Batch ${i + 1} - ${moment.utc(batch.productionTime).local().format('H:mm')}
        ${batch.isActive !== true ? ' - user deleted batch ' : ''}`}
        {renderTags(batch)}
      </span>
    )
  }

  const renderSummary = (text: string, value: string, style: string) => (
    <Card
      orientation='vertical'
      style={{
        background:
          'var(--fable-color-background-secondary-default) !important',
        padding: '24px',
        boxSizing: 'border-box',
      }}
    >
      <SummaryCard title={text} value={value} className={style} />
    </Card>
  )

  const renderTags = (batch: IBatchTemperature) => (
    <Flex gap='sm'>
      {batch.items.filter((bi) => bi.outOfCabinetOverdueTime).length > 0 && (
        <Tag
          size='sm'
          leadingIcon={<Clock />}
          testid='overdue-tag'
          style={{
            backgroundColor: 'var(--fable-color-background-secondary-default)',
            color: 'var(--fable-color-text-error)',
          }}
        >
          Overdue
        </Tag>
      )}

      {batch.programId !== null &&
        batch.temperatures!.filter((x) => !x.isBluetoothReading).length > 0 && (
          <Tag
            size='sm'
            leadingIcon={<Edit />}
            testid='manual-temperature-checks-tag'
            style={{
              backgroundColor: 'var(--fable-color-text-information)',
              color: 'var(--fable-color-button-brand-1-primary-text-default)',
            }}
          >
            Manual check
          </Tag>
        )}

      {batch.items.filter((bi) => bi.calculatedQuantity !== 0).length > 0 &&
        batch.items.filter(
          (bi) => bi.calculatedQuantity !== bi.quantityProduced,
        ).length > 0 && (
          <Tag
            size='sm'
            leadingIcon={<Edit />}
            testid='edited-batch-tag'
            style={{
              backgroundColor: 'var(--fable-color-background-warning-bold)',
              color: 'var(--fable-color-button-brand-1-primary-text-default)',
            }}
          >
            Edited batch
          </Tag>
        )}

      {batch.temperatures!.filter((x) => parseInt(x.temperature, 10) < 75)
        .length > 0 && (
        <Tag
          size='sm'
          leadingIcon={<AlertCircleFilled />}
          testid='failed-check-tag'
          style={{
            backgroundColor: 'var(--fable-color-background-error-bold-default)',
            color: 'var(--fable-color-button-brand-1-primary-text-default)',
          }}
        >
          Failed check
        </Tag>
      )}

      {batch.isUserCreated && (
        <Tag
          size='sm'
          leadingIcon={<Add />}
          testid='created-check-tag'
          style={{
            backgroundColor: 'var(--fable-color-background-nectar-bold)',
            color: 'var(--fable-color-button-brand-1-primary-text-default)',
          }}
        >
          Created
        </Tag>
      )}
    </Flex>
  )

  const renderTemperatureChecks = (batch: IBatchTemperature) => {
    if (batch.temperaturesCount === 0) {
      return
    }

    const temperatureCount: number[] = []

    for (let count = 0; count < batch.temperaturesCount; count++) {
      temperatureCount.push(count)
    }

    return (
      <Grid cols={12}>
        <GridItem colSpan={2}>&nbsp;</GridItem>
        <GridItem
          colSpan={{ lg: 12, md: 12, sm: 12, xs: 12 }}
          style={{
            border: '1px solid var(--fable-color-border-default, #bfbfbf)',
            margin: '12px 0',
            padding: '16px',
          }}
        >
          <Flex direction='column' gap='sm'>
            {temperatureCount.map((count) => (
              <TemperatureCheck
                key={count}
                batch={batch}
                temperatureCount={count}
              />
            ))}
          </Flex>
        </GridItem>
        <GridItem colSpan={2}>&nbsp;</GridItem>
      </Grid>
    )
  }

  return (
    <Box backgroundColor='primary'>
      <Card orientation='vertical' className='c-common-only-visible-in-print'>
        <Box padding='none'>
          {`Temperature check report from the ${moment(props.batchDate).format('DD/MM/YYYY')}`}
        </Box>
      </Card>

      {props.counterAvailability !== undefined &&
        props.counterAvailability.updatedBy !== '' && (
          <Card
            orientation='vertical'
            className='c-common-banner--warning c-common-hide-in-print'
            style={{ marginBottom: 24 }}
          >
            <Flex alignItems='center' gap='sm'>
              <InfoCircle
                width={20}
                height={20}
                style={{ marginLeft: '16px' }}
              />
              <Box>
                <Label1>
                  {`Counter ${props.counterAvailability.isActive ? 're-opened' : 'closed'}
                 at ${moment.utc(props.counterAvailability.updatedAt).local().format('hh.mma')}
                by ${props.counterAvailability.updatedBy!}`}
                  {!props.counterAvailability.isActive &&
                    ` due to ${props.counterAvailability.reasonDescription}.`}
                </Label1>
              </Box>
            </Flex>
          </Card>
        )}

      {props.batchTemperatures.batchTemperatures.length > 0 && (
        <Grid
          gap='lg'
          cols={{ xs: 1, sm: 1, md: 1, lg: 4 }}
          style={{ marginBottom: 32 }}
          testid='summary-card'
        >
          {renderSummary(
            'SKUs removed from display on time',
            `${props.batchTemperatures.skusRemovedFromDisplayOnTime}%`,
            props.batchTemperatures.skusRemovedFromDisplayOnTime > 0
              ? '#2e7d32'
              : 'var(--fable-color-text-error)',
          )}
          {renderSummary(
            'SKUs on display past removal time',
            `${props.batchTemperatures.skusOnDisplayPastRemovalTime}%`,
            props.batchTemperatures.skusOnDisplayPastRemovalTime > 0
              ? 'var(--fable-color-text-error)'
              : '#2e7d32',
          )}
          {renderSummary(
            'Failed temperature checks',
            props.batchTemperatures.failedTempChecks.toString(),
            props.batchTemperatures.failedTempChecks > 0
              ? 'var(--fable-color-text-error)'
              : '#2e7d32',
          )}
          {renderSummary(
            'Manual temperature checks',
            props.batchTemperatures.manualTemperatureChecks.toString(),
            '',
          )}
          {renderSummary(
            'Edited batches',
            props.batchTemperatures.editedBatches.toString(),
            '',
          )}
          {renderSummary(
            'Created batches',
            props.batchTemperatures.createdBatches.toString(),
            '',
          )}
          {renderSummary(
            'Planner completion',
            `${props.batchTemperatures.plannerCompletion}%`,
            props.batchTemperatures.plannerCompletion < 85
              ? 'var(--fable-color-text-error)'
              : '#2e7d32',
          )}
          {renderSummary(
            'Batch time compliance',
            `${props.batchTemperatures.batchTimeCompliance}%`,
            props.batchTemperatures.batchTimeCompliance < 85
              ? 'var(--fable-color-text-error)'
              : '#2e7d32',
          )}
        </Grid>
      )}

      {props.batchTemperatures.batchTemperatures.map((batch, i) => (
        <Box
          className='c-batch-container'
          backgroundColor='secondary'
          key={i}
          padding='none'
        >
          <Accordion className='c-common-hide-in-print' size='md'>
            {isMobile ? (
              <>
                <div className='c-batch-header-mobile'>
                  <div className='c-batch-tags-mobile'>{renderTags(batch)}</div>
                </div>
                <AccordionItem
                  className='c-common-hide-in-print'
                  key={batch.batchId}
                  label={
                    batch.programId == null
                      ? `${batch.isPizza ? 'Pizza' : ''}${batch.isPizza && batch.isIsbProduct ? ' and ' : ''}${batch.isIsbProduct ? 'ISB' : ''} - ${moment.utc(batch.productionTime).local().format('H:mm')}`
                      : `Batch ${i + 1} - ${moment.utc(batch.productionTime).local().format('H:mm')}${batch.isActive !== true ? ' - user deleted batch' : ''}`
                  }
                  labelAs='h3'
                  isDefaultOpen={false}
                  size='sm'
                  testid={`batch-accordion-item-${batch.batchId}`}
                >
                  {renderAccordionItemCard(batch)}
                </AccordionItem>
              </>
            ) : (
              <>
                <AccordionItem
                  className='c-common-hide-in-print'
                  key={batch.batchId}
                  label={' '}
                  labelComponent={
                    <Flex className='c-batch-label-row'>
                      <span className='c-batch-title'>
                        {batch.programId == null
                          ? `${batch.isPizza ? 'Pizza' : ''}${batch.isPizza && batch.isIsbProduct ? ' and ' : ''}${batch.isIsbProduct ? 'ISB' : ''} - ${moment.utc(batch.productionTime).local().format('H:mm')}`
                          : `Batch ${i + 1} - ${moment.utc(batch.productionTime).local().format('H:mm')}${batch.isActive !== true ? ' - user deleted batch' : ''}`}
                      </span>
                      <Flex className='c-batch-tags'>{renderTags(batch)}</Flex>
                    </Flex>
                  }
                  labelAs='h3'
                  isDefaultOpen={false}
                  size='md'
                  testid={`batch-accordion-item-${batch.batchId}`}
                  style={{ width: '100%' }}
                >
                  {renderAccordionItemCard(batch)}
                </AccordionItem>
              </>
            )}
          </Accordion>
          <Box className='c-common-only-visible-in-print' padding='none'>
            <Display1>{renderBatchTitle(batch, i)}</Display1>
            {renderAccordionItemCard(batch)}
          </Box>
        </Box>
      ))}

      <Card orientation='vertical' style={{ marginTop: 32 }}>
        <Flex direction='column' alignItems='center'>
          <Box className='c-common-only-visible-in-print'>
            {getPrintFooter()}
          </Box>
          <Button
            className='c-common-hide-in-print'
            onClick={() => window.print()}
            testid='print-button'
          >
            Print Report
          </Button>
        </Flex>
      </Card>
    </Box>
  )
}

export default TemperatureCheckView
